import type { BadgeProps } from '@elka/ui';
import type { DealQuotingStatus } from '@elka/types';

/**
 * Single source of truth for how a deal's CRM `stage` and quoting `quotingStatus`
 * are colored across the quotes app. Before this module the deals list rendered the
 * same status three different ways: the filter chips used a rich palette, the mobile
 * card used a muted (and colliding) Badge variant, and the table used progress dots.
 * The chip palette here is the canonical one; the Badge variants are derived from the
 * same color intent so the chip and the card can't drift apart again.
 *
 * Two consumer shapes:
 *  - `*ChipColors` → `{ selected, unselected }` Tailwind class pairs for FilterBar chips.
 *  - `*BadgeVariant` → an `@elka/ui` <Badge> variant for the card view.
 */

type ChipColor = { selected: string; unselected: string };
type BadgeVariant = NonNullable<BadgeProps['variant']>;

// --- Deal stage (only the quotes-relevant stages are surfaced; lead/qualified/lost
// are filtered out of the quotes deal lists, but kept here for completeness/safety). ---

export const STAGE_CHIP_COLORS: Record<string, ChipColor> = {
  lead: {
    selected: 'bg-gray-600 text-white',
    unselected: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  },
  qualified: {
    selected: 'bg-blue-600 text-white',
    unselected: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  },
  proposal: {
    selected: 'bg-yellow-500 text-white',
    unselected: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
  },
  negotiation: {
    selected: 'bg-orange-500 text-white',
    unselected: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
  },
  won: {
    selected: 'bg-green-600 text-white',
    unselected: 'bg-green-100 text-green-700 hover:bg-green-200',
  },
  lost: {
    selected: 'bg-red-600 text-white',
    unselected: 'bg-red-100 text-red-700 hover:bg-red-200',
  },
};

export const STAGE_BADGE_VARIANT: Record<string, BadgeVariant> = {
  lead: 'default',
  qualified: 'info',
  proposal: 'warning',
  negotiation: 'warning',
  won: 'success',
  lost: 'danger',
};

// --- Deal quoting status (the eight-step quoting workflow). The chip palette is the
// canonical encoding; the Badge variants below mirror the same hue family so the
// mobile card and the filter chip agree. ---

export const QUOTING_STATUS_CHIP_COLORS: Record<DealQuotingStatus, ChipColor> = {
  draft: {
    selected: 'bg-gray-600 text-white',
    unselected: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  },
  'boq-intake': {
    selected: 'bg-slate-500 text-white',
    unselected: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
  },
  estimating: {
    selected: 'bg-blue-600 text-white',
    unselected: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  },
  'supplier-pricing': {
    selected: 'bg-amber-500 text-white',
    unselected: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
  },
  'ready-to-quote': {
    selected: 'bg-orange-500 text-white',
    unselected: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
  },
  quoting: {
    selected: 'bg-indigo-600 text-white',
    unselected: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
  },
  quoted: {
    selected: 'bg-green-600 text-white',
    unselected: 'bg-green-100 text-green-700 hover:bg-green-200',
  },
  accepted: {
    selected: 'bg-emerald-600 text-white',
    unselected: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
  },
};

// Badge variants derived from the chip hue family above. The shared <Badge> palette is
// coarser than the chips (no distinct amber/orange/indigo), so adjacent steps that the
// chips distinguish collapse onto the nearest Badge tone — but the card no longer
// contradicts the chip (e.g. supplier-pricing reads warning in both, not warning-vs-info).
export const QUOTING_STATUS_BADGE_VARIANT: Record<DealQuotingStatus, BadgeVariant> = {
  draft: 'default',
  'boq-intake': 'default',
  estimating: 'info',
  'supplier-pricing': 'warning',
  'ready-to-quote': 'warning',
  quoting: 'info',
  quoted: 'success',
  accepted: 'success',
};
